import { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from 'react'

/**
 * B-new-1010 — `TermsAcceptanceGate` renders inside `AuthProvider`, which
 * sits ABOVE `<RouterProvider>` in `main.tsx` (B-new-100 layering: nothing
 * that can fire a `protectedDbProcedure` may mount above auth, so the gate
 * can't just move under the router). That means it can't call
 * `useRouter()`/`useLocation()` — those hooks require being inside
 * `RouterProvider`'s context, which doesn't exist yet at the point the gate
 * renders.
 *
 * This is a minimal, structurally-typed bridge: `main.tsx` hands the SAME
 * router instance it passes to `RouterProvider` into this provider (no
 * second router is ever created), and anything above the router tree that
 * needs to react to client-side navigation reads the pathname through
 * `useReactivePathname()` instead of a one-shot `window.location.pathname`
 * read.
 *
 * Structural (not `ReturnType<typeof createRouter>`) on purpose: importing
 * the real router type here would pull `routeTree.gen` into this module's
 * (and therefore the gate's) import graph, which every existing
 * `TermsAcceptanceGate` test renders bare, with no router at all. Kept
 * structural, a bare-router test render just uses the `window.location`
 * fallback below, unchanged.
 */
interface RouterLocationSource {
  history: {
    subscribe(onChange: () => void): () => void
    location: { pathname: string }
  }
}

const RouterLocationContext = createContext<RouterLocationSource | null>(null)

export function RouterLocationProvider({
  router,
  children,
}: {
  router: RouterLocationSource
  children: React.ReactNode
}) {
  return (
    <RouterLocationContext.Provider value={router}>{children}</RouterLocationContext.Provider>
  )
}

const NOOP_SUBSCRIBE = () => () => {}
const WINDOW_SNAPSHOT = () => (typeof window !== 'undefined' ? window.location.pathname : '/')
const SERVER_SNAPSHOT = () => '/'

/**
 * Reactive pathname for anything mounted above `RouterProvider`. Re-renders
 * on every client-side navigation via `router.history.subscribe` (fires
 * synchronously on push/replace — unlike `router.subscribe('onResolved')`,
 * which waits for loaders and would leave a fail-open window on a slow
 * route). Falls back to a single `window.location.pathname` read (never
 * changes on its own) when no provider is mounted, e.g. every existing test
 * that renders `TermsAcceptanceGate` bare.
 */
export function useReactivePathname(): string {
  const source = useContext(RouterLocationContext)

  const subscribe = useMemo(
    () => (source ? source.history.subscribe : NOOP_SUBSCRIBE),
    [source],
  )
  const getSnapshot = useCallback(
    () => (source ? source.history.location.pathname : WINDOW_SNAPSHOT()),
    [source],
  )

  return useSyncExternalStore(subscribe, getSnapshot, SERVER_SNAPSHOT)
}
